import { useState } from 'react';
import { Copy, Check, File } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface FilePreviewProps {
  path: string;
  content: string;
}

function getLanguage(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  const langMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'tsx',
    js: 'javascript',
    jsx: 'jsx',
    py: 'python',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    java: 'java',
    kt: 'kotlin',
    json: 'json',
    yml: 'yaml',
    yaml: 'yaml',
    md: 'markdown',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    css: 'css',
    scss: 'scss',
    html: 'html',
    xml: 'xml',
    sql: 'sql',
    graphql: 'graphql',
    dockerfile: 'docker',
    toml: 'toml',
    ini: 'ini',
    env: 'bash',
  };

  // Handle special filenames
  const filename = path.split('/').pop()?.toLowerCase() || '';
  if (filename === 'dockerfile') return 'docker';
  if (filename === 'makefile') return 'makefile';
  if (filename === '.gitignore' || filename === '.eslintignore') return 'bash';
  if (filename === 'codeowners') return 'bash';

  return langMap[ext] || 'text';
}

export default function FilePreview({ path, content }: FilePreviewProps) {
  const [copied, setCopied] = useState(false);
  const language = getLanguage(path);
  const fileName = path.split('/').pop() || path;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return (
    <div className="rounded-xl border border-dark-border bg-dark-card overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border bg-surface-50/50">
        <div className="flex items-center gap-2 min-w-0">
          <File className="h-4 w-4 text-gray-500 shrink-0" />
          <span className="text-sm text-gray-400 truncate font-mono">
            {path}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
            text-gray-400 hover:text-white hover:bg-surface-200 transition-all duration-150 shrink-0"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-400" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <SyntaxHighlighter
          language={language}
          style={oneDark}
          showLineNumbers
          wrapLongLines
          customStyle={{
            margin: 0,
            padding: '16px',
            background: 'transparent',
            fontSize: '13px',
            lineHeight: '1.6',
            minHeight: '100%',
          }}
          lineNumberStyle={{
            minWidth: '3em',
            paddingRight: '1em',
            color: '#4a4a5a',
            userSelect: 'none',
          }}
        >
          {content}
        </SyntaxHighlighter>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-dark-border text-xs text-gray-600">
        <span>{fileName}</span>
        <span>
          {content.split('\n').length} lines &middot; {language}
        </span>
      </div>
    </div>
  );
}
